import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useMsal } from '@azure/msal-react';
import { AppHeader } from '../../shared/components/AppComponents';
import { apiFetch, apiFetchJson } from '../../apiClient';
import UploadPanel from './UploadPanel';
import SaxFingeringDiagram from './SaxFingeringDiagram';
import PianoRoll from './PianoRoll';
import NoteDisplay from './NoteDisplay';
import PlaybackControls from './PlaybackControls';
import { createAudioEngine } from './AudioEngine';
import './SaxTutorApp.css';

const unwrap = (data) => {
  const arr = Array.isArray(data) ? data : (data?.$values || []);
  return arr.filter(item => item && !item.$ref);
};

function SaxTutorApp({ user }) {
  const { t } = useTranslation();
  const { instance } = useMsal();
  const [songs, setSongs] = useState([]);
  const [currentSong, setCurrentSong] = useState(null);
  const [currentNoteIndex, setCurrentNoteIndex] = useState(-1);
  const [isPlaying, setIsPlaying] = useState(false);
  const [tempo, setTempo] = useState(120);
  const [saxType, setSaxType] = useState('Alto');
  const [isLooping, setIsLooping] = useState(false);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [view, setView] = useState('upload'); // 'upload' | 'player'

  const audioEngineRef = useRef(null);
  const playbackTimerRef = useRef(null);

  useEffect(() => {
    audioEngineRef.current = createAudioEngine();
    return () => {
      if (audioEngineRef.current) audioEngineRef.current.stop();
    };
  }, []);

  const loadSongs = useCallback(async () => {
    try {
      const data = await apiFetchJson(instance, '/api/saxtool/songs');
      setSongs(unwrap(data));
    } catch (e) {
      console.error('Failed to load songs:', e);
    }
  }, [instance]);

  useEffect(() => { loadSongs(); }, [loadSongs]);

  const handleUpload = useCallback(async (file, uploadType) => {
    try {
      setUploading(true);
      setError(null);
      const endpoint = uploadType === 'musicxml'
        ? `/api/saxtool/upload-musicxml?saxType=${saxType}`
        : `/api/saxtool/upload-image?saxType=${saxType}`;

      const formData = new FormData();
      formData.append('file', file);

      const res = await apiFetch(instance, endpoint, {
        method: 'POST',
        body: formData,
        headers: {} // let browser set content-type for FormData
      });

      if (res.ok) {
        const song = await res.json();
        const notes = unwrap(song.notes || song.Notes);
        song.notes = notes;
        setCurrentSong(song);
        setTempo(song.tempo || song.Tempo || 120);
        setCurrentNoteIndex(-1);
        setView('player');
        loadSongs();
      } else {
        const errText = await res.text();
        setError(`${t('saxTutor.uploadFailed')}: ${errText}`);
      }
    } catch (e) {
      setError(`${t('saxTutor.uploadError')}: ${e.message}`);
    } finally {
      setUploading(false);
    }
  }, [instance, saxType, loadSongs, t]);

  const handleSelectSong = useCallback(async (songId) => {
    try {
      setLoading(true);
      const song = await apiFetchJson(instance, `/api/saxtool/songs/${songId}`);
      const notes = unwrap(song.notes || song.Notes);
      song.notes = notes;
      setCurrentSong(song);
      setTempo(song.tempo || song.Tempo || 120);
      setCurrentNoteIndex(-1);
      setView('player');
    } catch (e) {
      setError(`${t('saxTutor.loadSongError')}: ${e.message}`);
    } finally {
      setLoading(false);
    }
  }, [instance, t]);

  const handleDeleteSong= useCallback(async (songId) => {
    try {
      await apiFetchJson(instance, `/api/saxtool/songs/${songId}`, { method: 'DELETE' });
      setSongs(prev => prev.filter(s => (s.id || s.Id) !== songId));
      if (currentSong && (currentSong.id || currentSong.Id) === songId) {
        setCurrentSong(null);
        setView('upload');
      }
    } catch (e) {
      console.error('Failed to delete song:', e);
    }
  }, [instance, currentSong]);

  const stopPlayback = useCallback(() => {
    setIsPlaying(false);
    if (playbackTimerRef.current) {
      clearTimeout(playbackTimerRef.current);
      playbackTimerRef.current = null;
    }
    if (audioEngineRef.current) audioEngineRef.current.stop();
  }, []);

  const playNote = useCallback((noteIndex) => {
    if (!currentSong || !currentSong.notes) return;
    const notes = currentSong.notes;
    if (noteIndex >= notes.length) {
      if (isLooping) {
        setCurrentNoteIndex(0);
        playNote(0);
        return;
      }
      stopPlayback();
      setCurrentNoteIndex(-1);
      return;
    }

    const note = notes[noteIndex];
    setCurrentNoteIndex(noteIndex);

    if (!note.isRest && !note.IsRest) {
      const noteName = note.noteName || note.NoteName;
      if (audioEngineRef.current && noteName && noteName !== 'rest') {
        const beats = note.durationBeats || note.DurationBeats || 1;
        const durationMs = (beats / (tempo / 60)) * 1000;
        audioEngineRef.current.playNote(noteName, durationMs * 0.9);
      }
    }

    const beats = note.durationBeats || note.DurationBeats || 1;
    const durationMs = (beats / (tempo / 60)) * 1000;

    playbackTimerRef.current = setTimeout(() => {
      playNote(noteIndex + 1);
    }, durationMs);
  }, [currentSong, tempo, isLooping, stopPlayback]);

  const startPlayback = useCallback(() => {
    if (!currentSong || !currentSong.notes || currentSong.notes.length === 0) return;
    setIsPlaying(true);
    const startIndex = currentNoteIndex >= 0 ? currentNoteIndex : 0;
    playNote(startIndex);
  }, [currentSong, currentNoteIndex, playNote]);

  const togglePlayback = useCallback(() => {
    if (isPlaying) {
      stopPlayback();
    } else {
      startPlayback();
    }
  }, [isPlaying, stopPlayback, startPlayback]);

  const handleSeek = useCallback((index) => {
    stopPlayback();
    setCurrentNoteIndex(index);
  }, [stopPlayback]);

  const currentNote = currentSong?.notes?.[currentNoteIndex] || null;
  const currentNoteName = currentNote
    ? (currentNote.noteName || currentNote.NoteName || '')
    : '';

  return (
    <div className="saxtutor-app">
      <AppHeader title={`🎷 ${t('saxTutor.title')}`} user={user} />

      {error && (
        <div className="saxtutor-error" role="alert">
          {error}
          <button onClick={() => setError(null)} aria-label="Dismiss error">×</button>
        </div>
      )}

      {view === 'upload' && (
        <UploadPanel
          onUpload={handleUpload}
          uploading={uploading}
          songs={songs}
          onSelectSong={handleSelectSong}
          onDeleteSong={handleDeleteSong}
          saxType={saxType}
          onSaxTypeChange={setSaxType}
          loading={loading}
        />
      )}

      {view === 'player' && currentSong && (
        <div className="saxtutor-player">
          <div className="saxtutor-player-header">
            <button className="saxtutor-back-btn" onClick={() => { stopPlayback(); setView('upload'); }} aria-label={t('saxTutor.songLibrary')}>
              ← {t('saxTutor.songLibrary')}
            </button>
            <h2>{currentSong.title || currentSong.Title}</h2>
          </div>

          <NoteDisplay
            notes={currentSong.notes}
            currentNoteIndex={currentNoteIndex}
            onSeek={handleSeek}
          />

          <div className="saxtutor-main-panels">
            <div className="saxtutor-fingering-panel">
              <SaxFingeringDiagram noteName={currentNoteName} />
            </div>
            <div className="saxtutor-pianoroll-panel">
              <PianoRoll
                notes={currentSong.notes}
                currentNoteIndex={currentNoteIndex}
                onSeek={handleSeek}
              />
            </div>
          </div>

          <PlaybackControls
            isPlaying={isPlaying}
            onTogglePlay={togglePlayback}
            onStop={() => { stopPlayback(); setCurrentNoteIndex(-1); }}
            tempo={tempo}
            onTempoChange={setTempo}
            isLooping={isLooping}
            onToggleLoop={() => setIsLooping(!isLooping)}
            saxType={saxType}
            onSaxTypeChange={setSaxType}
            currentNoteIndex={currentNoteIndex}
            totalNotes={currentSong.notes?.length || 0}
          />
        </div>
      )}
    </div>
  );
}

export default SaxTutorApp;
